import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import FormTextField from 'Components/FormTextField'
import PTAFormField from 'Components/PTAFormField'
import StyledForm from 'Components/StyledForm/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import AllowedApiClients from 'Services/common/Features/AllowedApiClient/Components/AllowedApiClients'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { SELECTED_SERVICE_PROVIDER_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Hooks/useServiceProvidersWithParams'
import {
  useCreateSingleServiceProviderMutation,
  useUpdateSingleServiceProviderMutation
} from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderApi'
import {
  type ServiceProvider,
  ServiceProviderType
} from 'Services/ServiceProviderManagement/Features/ServiceProvider/types'

import type { FieldValidationErrors } from 'Services/ServiceProviderManagement/types'
import { type Translation, useTypedSelector } from 'Store'
import type { ServiceProviderManagementError } from 'Util/ApiClient'

import { CopyObjectKeys, useCopyObjectContext } from 'Util/Context/copyObjectContext'

import {
  ALLOWED_API_CLIENTS_CONTAINER_MAX_WIDTH,
  FORM_FIELD_DEFAULT_MIN_WIDTH,
  IS_HSL_SERVICE_ENVIRONMENT,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PTA_PARAM_KEY,
  Service
} from 'Util/constants'
import { formatToUTCISO } from 'Util/formatters'
import { useValidationErrors } from 'Util/hooks/useValidationErrors'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: FieldValidationErrors = {
  service_provider_id: [],
  type: [],
  start_date: [],
  name: [],
  customer_reference_name: [],
  business_id: [],
  s365t_account_id: [],
  billed_at_sales_point_level: [],
  transfer_sales_to_financial_system: [],
  register_file_path: [],
  register_file_name: [],
  register_ok_file_name: [],
  register_not_ok_file_name: [],
  vrk_queries_enabled: [],
  cpu_url: [],
  pusatec_channel: [],
  source: []
}

interface ServiceProviderFormProps {
  serviceProvider?: ServiceProvider
  successBaseUrl: string
}

export default function ServiceProviderForm({
  serviceProvider,
  successBaseUrl
}: Readonly<ServiceProviderFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!serviceProvider
  const { user } = useTypedSelector(state => state.user)

  const [createSingleServiceProvider, createStatus] = useCreateSingleServiceProviderMutation()
  const [updateSingleServiceProvider, updateStatus] = useUpdateSingleServiceProviderMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const { getCopiedObject, clearCopiedObject } = useCopyObjectContext()
  const { selectedAuthorityCode } = useAuthorityContext()

  const serviceProviderDefaultValues: Partial<ServiceProvider> = {
    pta: IS_WALTTI_SERVICE_ENVIRONMENT ? selectedAuthorityCode : undefined,
    active: true,
    ...(IS_HSL_SERVICE_ENVIRONMENT
      ? {
          billed_at_sales_point_level: false,
          transfer_sales_to_financial_system: false
        }
      : {
          vrk_queries_enabled: false
        })
  }

  const copiedServiceProviderData = isEdit
    ? null
    : getCopiedObject<ServiceProvider>(CopyObjectKeys.SERVICE_PROVIDER)

  const [serviceProviderDetails, setServiceProviderDetails] = useState<Partial<ServiceProvider>>(
    (isEdit && serviceProvider) || copiedServiceProviderData || { ...serviceProviderDefaultValues }
  )
  const { fieldErrors, setValidationErrors, getFieldErrorHelperText, getErrorProps } =
    useValidationErrors(fieldErrorsDefaultState)

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>(
    serviceProviderDetails?.client_read ?? []
  )
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>(
    serviceProviderDetails?.client_write ?? []
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress clearCopiedObject, suppress copiedServiceProviderData
  useEffect(() => {
    if (copiedServiceProviderData) {
      clearCopiedObject(CopyObjectKeys.SERVICE_PROVIDER)
    }
  }, [])

  useEffect(() => {
    if (isEdit && serviceProvider) {
      setServiceProviderDetails(serviceProvider)
    }
  }, [serviceProvider, isEdit])

  const handleChange = (
    value: string | string[] | Translation[] | number | boolean | undefined,
    field: keyof ServiceProvider
  ) => {
    if (value !== undefined) {
      setServiceProviderDetails(
        prevState =>
          ({
            ...prevState,
            [field]: value
          }) as ServiceProvider
      )
    }
    if (field === 's365t_account_id') {
      setServiceProviderDetails(
        prevState =>
          ({
            ...prevState,
            s365t_account_id: value === '' ? null : value,
            billed_at_sales_point_level: prevState.s365t_account_id
              ? prevState.billed_at_sales_point_level
              : false,
            transfer_sales_to_financial_system: prevState.s365t_account_id
              ? prevState.transfer_sales_to_financial_system
              : false
          }) as ServiceProvider
      )
    }
  }

  const handleDateTimePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = formatToUTCISO(value)
    }

    setServiceProviderDetails(prevState => ({
      ...prevState,
      [name]: formattedValue
    }))
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const serviceProviderData = {
      ...serviceProviderDetails,
      client_read: selectedReadClients,
      client_write: selectedWriteClients
    }

    const mutation = isEdit
      ? updateSingleServiceProvider({
          ...serviceProviderData,
          serviceProviderId: serviceProvider.service_provider_id
        })
      : createSingleServiceProvider({
          ...serviceProviderData,
          pta: IS_HSL_SERVICE_ENVIRONMENT ? user?.pta : serviceProviderData.pta
        })

    mutation
      .unwrap()
      .then((serviceProviderResponse: ServiceProvider) => {
        SnackbarUtils.success(t`Service provider successfully saved.`)
        navigate(
          appendQueryParamsToUrl(successBaseUrl, {
            [SELECTED_SERVICE_PROVIDER_PARAMETER_KEY]: serviceProviderResponse.service_provider_id,
            [PTA_PARAM_KEY]: serviceProviderResponse.pta
          })
        )
      })
      .catch(({ data: errorResponse }: { data: ServiceProviderManagementError }) => {
        const { violations } = errorResponse
        setValidationErrors(violations)
        SnackbarUtils.error(t`There was an error saving the service provider.`)
      })
  }

  const handleServiceProviderTypeChange = (value: string, field: keyof ServiceProvider) => {
    setServiceProviderDetails(
      prevState =>
        ({
          ...prevState,
          [field]: value,
          register_file_name:
            value === ServiceProviderType.SERVICE_POINT ? prevState.register_file_name : null,
          register_file_path:
            value === ServiceProviderType.SERVICE_POINT ? prevState.register_file_path : null,
          register_ok_file_name:
            value === ServiceProviderType.SERVICE_POINT ? prevState.register_ok_file_name : null,
          register_not_ok_file_name:
            value === ServiceProviderType.SERVICE_POINT ? prevState.register_not_ok_file_name : null
        }) as ServiceProvider
    )
  }

  const commonServiceProviderTypes = [
    {
      label: t`Traffic contractor`,
      value: ServiceProviderType.TRAFFIC_CONTRACTOR
    },
    {
      label: t`U Traffic contractor`,
      value: ServiceProviderType.U_TRAFFIC_CONTRACTOR
    },
    {
      label: t`Sales point`,
      value: ServiceProviderType.SALES_POINT
    },
    {
      label: t`Service point`,
      value: ServiceProviderType.SERVICE_POINT
    },
    {
      label: t`MaaS`,
      value: ServiceProviderType.MAAS
    },
    {
      label: t`Other`,
      value: ServiceProviderType.OTHER
    }
  ]

  const walttiServiceProviderTypes = [
    ...commonServiceProviderTypes,
    {
      label: t`Ticket machine`,
      value: ServiceProviderType.TICKET_MACHINE
    },
    {
      label: t`Webshop`,
      value: ServiceProviderType.WEBSHOP
    },
    {
      label: t`Mobile`,
      value: ServiceProviderType.MOBILE
    },
    {
      label: t`Event ticket`,
      value: ServiceProviderType.EVENT_TICKET
    }
  ]

  const hslServiceProviderTypes = [
    ...commonServiceProviderTypes,
    {
      label: t`HSL digital channel`,
      value: ServiceProviderType.HSL_DIGITAL_CHANNEL
    }
  ]

  const serviceProviderTypes = IS_HSL_SERVICE_ENVIRONMENT
    ? hslServiceProviderTypes
    : walttiServiceProviderTypes

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
          {!isEdit && IS_WALTTI_SERVICE_ENVIRONMENT && (
            <Box>
              <PTAFormField
                value={serviceProviderDetails.pta}
                onChange={value => handleChange(value, 'pta')}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 3 }}>
            {!isEdit && (
              <Box>
                <FormTextField
                  name="service_provider_id"
                  required
                  label={t`Service provider ID`}
                  value={serviceProviderDetails?.service_provider_id ?? ''}
                  onChange={e => handleChange(e.target.value, 'service_provider_id')}
                  maxLength={9}
                  {...getErrorProps('service_provider_id')}
                />
              </Box>
            )}
            <Box>
              <FormTextField
                name="name"
                required
                label={t`Name`}
                value={serviceProviderDetails?.name ?? ''}
                onChange={e => handleChange(e.target.value, 'name')}
                {...getErrorProps('name')}
              />
            </Box>
            <FormControl sx={{ minWidth: FORM_FIELD_DEFAULT_MIN_WIDTH }} required>
              <InputLabel size="small">{t`Type`}</InputLabel>
              <Select
                name="type"
                required
                size="small"
                label={t`Type`}
                value={serviceProviderDetails?.type ?? ''}
                error={fieldErrors.type?.length > 0}
                onChange={e => handleServiceProviderTypeChange(e.target.value, 'type')}
                sx={{
                  minWidth: 280
                }}
              >
                {serviceProviderTypes.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText error={fieldErrors.type?.length > 0}>
                {getFieldErrorHelperText('type')}
              </FormHelperText>
            </FormControl>
          </Box>
          <Box>
            <StyledLabel>{t`Start date`}</StyledLabel>
            <FormControl sx={{ minWidth: FORM_FIELD_DEFAULT_MIN_WIDTH }} size="small" required>
              <CustomDateTimePicker
                onChange={handleDateTimePickerChange}
                name="start_date"
                value={
                  serviceProviderDetails?.start_date
                    ? new Date(serviceProviderDetails?.start_date)
                    : null
                }
                label={t`Start date`}
                isRequired={true}
                isError={fieldErrors.start_date?.length > 0}
                helperText={getFieldErrorHelperText('start_date')}
              />
            </FormControl>
          </Box>
          <Box>
            <StyledLabel>{t`Business information`}</StyledLabel>
            <FormTextField
              name="business_id"
              label={t`Business id`}
              value={serviceProviderDetails?.business_id ?? ''}
              onChange={e => handleChange(e.target.value, 'business_id')}
              {...getErrorProps('business_id')}
            />
          </Box>
          {IS_HSL_SERVICE_ENVIRONMENT && (
            <>
              <FormTextField
                name="s365t_account_id"
                label={t`S365 account id`}
                value={serviceProviderDetails?.s365t_account_id ?? ''}
                sx={{ minWidth: 240, mr: 3 }}
                onChange={e =>
                  handleChange(e.target.value.replaceAll(/\D/g, ''), 's365t_account_id')
                }
                {...getErrorProps('s365t_account_id')}
              />
              <Box>
                <FormTextField
                  name="customer_reference_name"
                  required
                  label={t`Customer reference name`}
                  value={serviceProviderDetails?.customer_reference_name ?? ''}
                  onChange={e => handleChange(e.target.value, 'customer_reference_name')}
                  {...getErrorProps('customer_reference_name')}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={serviceProviderDetails.billed_at_sales_point_level}
                        disabled={!serviceProviderDetails.s365t_account_id}
                        onChange={e =>
                          handleChange(e.target.checked, 'billed_at_sales_point_level')
                        }
                        name="billed_at_sales_point_level"
                        color="primary"
                      />
                    }
                    label={t`Billed at sales point level`}
                  />
                  <FormHelperText error={fieldErrors.billed_at_sales_point_level.length > 0}>
                    {getFieldErrorHelperText('billed_at_sales_point_level')}
                  </FormHelperText>
                </FormGroup>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={serviceProviderDetails.transfer_sales_to_financial_system}
                        disabled={!serviceProviderDetails.s365t_account_id}
                        onChange={e =>
                          handleChange(e.target.checked, 'transfer_sales_to_financial_system')
                        }
                        name="transfer_sales_to_financial_system"
                        color="primary"
                      />
                    }
                    label={t`Transfer sales to financial system`}
                  />
                  <FormHelperText error={fieldErrors.transfer_sales_to_financial_system.length > 0}>
                    {getFieldErrorHelperText('transfer_sales_to_financial_system')}
                  </FormHelperText>
                </FormGroup>
              </Box>
            </>
          )}
          {serviceProviderDetails.type === ServiceProviderType.SERVICE_POINT &&
            IS_HSL_SERVICE_ENVIRONMENT && (
              <Box>
                <StyledLabel>{t`Other`}</StyledLabel>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <FormTextField
                    name="register_file_path"
                    required
                    label={t`Register file path`}
                    value={serviceProviderDetails?.register_file_path}
                    onChange={e => handleChange(e.target.value, 'register_file_path')}
                    {...getErrorProps('register_file_path')}
                  />
                  <FormTextField
                    name="register_file_name"
                    required
                    label={t`Register file name`}
                    value={serviceProviderDetails?.register_file_name}
                    onChange={e => handleChange(e.target.value, 'register_file_name')}
                    {...getErrorProps('register_file_name')}
                  />
                  <FormTextField
                    name="register_ok_file_name"
                    required
                    label={t`Register OK file name`}
                    value={serviceProviderDetails?.register_ok_file_name}
                    onChange={e => handleChange(e.target.value, 'register_ok_file_name')}
                    {...getErrorProps('register_ok_file_name')}
                  />
                  <FormTextField
                    name="register_not_ok_file_name"
                    required
                    label={t`Register not OK file name`}
                    value={serviceProviderDetails?.register_not_ok_file_name}
                    onChange={e => handleChange(e.target.value, 'register_not_ok_file_name')}
                    {...getErrorProps('register_not_ok_file_name')}
                  />
                </Box>
              </Box>
            )}
          {IS_WALTTI_SERVICE_ENVIRONMENT && (
            <Box>
              <StyledLabel>{t`Other`}</StyledLabel>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <FormTextField
                  name="cpu_url"
                  label={t`CPU url`}
                  value={serviceProviderDetails?.cpu_url}
                  onChange={e => handleChange(e.target.value, 'cpu_url')}
                  {...getErrorProps('cpu_url')}
                />
                <FormTextField
                  name="pusatec_channel"
                  label={t`Pusatec channel`}
                  value={serviceProviderDetails?.pusatec_channel}
                  onChange={e => handleChange(e.target.value, 'pusatec_channel')}
                  {...getErrorProps('pusatec_channel')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={serviceProviderDetails.vrk_queries_enabled}
                      onChange={e => handleChange(e.target.checked, 'vrk_queries_enabled')}
                      name="vrk_queries_enabled"
                      color="primary"
                    />
                  }
                  label={t`VRK queries enabled`}
                />
                <FormHelperText error={fieldErrors.vrk_queries_enabled.length > 0}>
                  {getFieldErrorHelperText('vrk_queries_enabled')}
                </FormHelperText>
              </Box>
            </Box>
          )}
          <Box>
            <StyledLabel>{t`Source`}</StyledLabel>
            <FormTextField
              name="source"
              label={t`Source`}
              value={serviceProviderDetails?.source ?? ''}
              onChange={e => handleChange(e.target.value, 'source')}
              maxLength={256}
              {...getErrorProps('source')}
            />
          </Box>
          <Box sx={{ maxWidth: ALLOWED_API_CLIENTS_CONTAINER_MAX_WIDTH, py: 2 }}>
            <AllowedApiClients
              service={Service.SERVICE_PROVIDER_MANAGEMENT}
              selectedReadClients={selectedReadClients}
              selectedWriteClients={selectedWriteClients}
              setSelectedReadClients={setSelectedReadClients}
              setSelectedWriteClients={setSelectedWriteClients}
            />
          </Box>
          <CustomLoadingButton
            isLoading={isSubmitLoading}
            type="submit"
            size="large"
            iconPosition="end"
          >
            {t`Save service provider`}
          </CustomLoadingButton>
        </StyledForm>
      </Grid>
    </Grid>
  )
}
